import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccountDocumentBasicResponse,
  AccountDocumentForm,
  AccountDocumentFormSchema,
  IAccountRespSchema,
  SigningOrderEnum,
} from '@monetize/types/app';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  useCreateAccountDocument,
  useUpdateAccountDocument,
} from '../../../../api/accountsService';
import {
  MBox,
  MButton,
  MCenterModal,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MRadio,
  MRadioGroup,
  MStack,
} from '../../../../components/Monetize';
import { MFileDragDropUpload } from '../../../../components/Monetize/MFileDragDropUpload';
import { getAccountDocumentDetails } from '../../../../constants/routes';

interface AccountDocumentFormModalProps {
  isOpen: boolean;
  account: IAccountRespSchema;
  accountDocument?: AccountDocumentBasicResponse;
  onClose: () => void;
}

export const AccountDocumentFormModal = ({
  isOpen,
  onClose,
  account,
  accountDocument: baseData,
}: AccountDocumentFormModalProps) => {
  const { id: accountId } = account;
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset,
  } = useForm({
    resolver: zodResolver(AccountDocumentFormSchema),
    defaultValues: {},
    mode: 'onChange',
  });

  const isValid = Object.keys(errors).length === 0;
  const isEditing = !!baseData;

  useEffect(() => {
    if (isEditing) {
      reset(baseData);
    }
  }, [isEditing, baseData, reset]);

  const {
    isPending: isCreatingAccountDocument,
    mutate: doCreateAccountDocument,
  } = useCreateAccountDocument({
    onSuccess: (createdAccountDocument) => {
      navigate(getAccountDocumentDetails(accountId, createdAccountDocument.id));
    },
  });

  const {
    mutateAsync: doUpdateAccountDocument,
    isPending: isUpdatingAccountDocument,
  } = useUpdateAccountDocument({
    onSuccess: () => {
      onClose();
    },
  });

  const onDropAccepted = useCallback(async (acceptedFiles: File) => {
    setAttachmentFile(acceptedFiles);
  }, []);

  const onSubmit = async (payload: AccountDocumentForm) => {
    if (!isEditing) {
      doCreateAccountDocument({
        accountId,
        payload: { ...payload, file: attachmentFile },
      });
    } else {
      doUpdateAccountDocument({
        accountId,
        accountDocumentId: baseData?.id,
        payload,
      });
    }
  };

  return (
    <MCenterModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle={isEditing ? 'Edit Document' : 'New Document'}
      size="lg"
      closeOnEsc={false}
      closeOnOverlayClick={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton onClick={onClose} variant="cancel" minW="auto">
            Cancel
          </MButton>
          <MButton
            data-testid="account-document-form-submit"
            variant="primary"
            isLoading={isCreatingAccountDocument || isUpdatingAccountDocument}
            onClick={handleSubmit(onSubmit)}
            isDisabled={!isDirty || !isValid || !attachmentFile}
            minW="auto"
          >
            Save
          </MButton>
        </MStack>
      )}
      modalHeaderProps={{ display: 'flex', alignItems: 'center' }}
    >
      <MBox>
        <form
          id="account-document-form"
          data-testid="account-document-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
            <MGridItem colSpan={12}>
              <MFormField label="Name" error={errors?.name} isRequired>
                <Controller
                  name={`name`}
                  control={control}
                  render={({ field: { value, ...field } }) => (
                    <MInput value={value || ''} {...field} />
                  )}
                />
              </MFormField>
            </MGridItem>
            <MGridItem colSpan={12}>
              <MFormField label="Description" error={errors?.description}>
                <Controller
                  name={`description`}
                  control={control}
                  render={({ field: { value, ...field } }) => (
                    <MInput value={value || ''} {...field} />
                  )}
                />
              </MFormField>
            </MGridItem>

            {isEditing && (
              <MGridItem colSpan={12}>
                <MFormField label="Signing Order" error={errors?.signingOrder}>
                  <Controller
                    name="signingOrder"
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <MRadioGroup
                        onChange={(val) => onChange(val as SigningOrderEnum)}
                        value={value || SigningOrderEnum.EXTERNAL_FIRST}
                        {...rest}
                      >
                        <MStack columnGap={5}>
                          <MRadio
                            value={SigningOrderEnum.EXTERNAL_FIRST}
                            width="fit-content"
                          >
                            Send to{' '}
                            {account?.accountName ? (
                              <strong>{account?.accountName}</strong>
                            ) : (
                              'external account contact'
                            )}{' '}
                            first
                          </MRadio>
                          <MRadio
                            value={SigningOrderEnum.INTERNAL_FIRST}
                            width="fit-content"
                          >
                            Send to internal contact first
                          </MRadio>
                          <MRadio
                            value={SigningOrderEnum.INSTANTANEOUS}
                            width="fit-content"
                          >
                            Send to both contacts at the same time
                          </MRadio>
                          <MRadio
                            value={SigningOrderEnum.EXTERNAL_ONLY}
                            width="fit-content"
                          >
                            Only require signature from customer{' '}
                            <strong>
                              {account?.accountName
                                ? account?.accountName
                                : 'customer'}
                            </strong>
                          </MRadio>
                        </MStack>
                      </MRadioGroup>
                    )}
                  />
                </MFormField>
              </MGridItem>
            )}
            {!isEditing && (
              <MGridItem colSpan={12}>
                <MFormField label="File" isRequired>
                  <MFileDragDropUpload
                    w="full"
                    accept={['pdf']}
                    label="Drag and Drop your file here or Browse"
                    onFileUpload={onDropAccepted}
                    maxFileSize={10}
                    innerContainerProps={{ p: '5', bg: 'white' }}
                    selectedFile={attachmentFile}
                  />
                </MFormField>
              </MGridItem>
            )}
          </MGrid>
        </form>
      </MBox>
    </MCenterModal>
  );
};
